package dev.youndie.site.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobwebx.markdown.markdown
import dev.youndie.site.blog.BLOG_ENTRIES
import dev.youndie.site.components.widgets.Chip
import dev.youndie.site.model.LIBRARIES
import dev.youndie.site.model.SITE_NAME
import dev.youndie.site.model.SITE_URL
import dev.youndie.site.model.blogPostingJsonLd
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text

/**
 * Front matter is the only place a post says what it is about, so it feeds
 * [PageLayoutData] directly. Doing it here rather than in the composable keeps a
 * single owner of the head tags — [PageLayout] — instead of two racing writers.
 */
@InitRoute
fun initMarkdownLayout(ctx: InitRouteContext) {
    val frontMatter = ctx.markdown?.frontMatter
    val title = frontMatter?.get("title")?.singleOrNull()
    val description = frontMatter?.get("description")?.singleOrNull()
    val date = frontMatter?.get("date")?.singleOrNull()

    // `searchTitle` is what a person would type; `title` is what reads well above the
    // article. They are different jobs and only one of them fits in a search result,
    // so a post may carry both. Without it the headline does double duty, which is how
    // a good title ends up matching nothing.
    val searchTitle = frontMatter?.get("searchTitle")?.singleOrNull() ?: title

    val route = ctx.route.path.trimEnd('/')
    val image = "$route.png"

    ctx.data.add(
        PageLayoutData(
            hasMarkdownCopy = true,
            title = if (searchTitle != null) "$searchTitle — $SITE_NAME" else SITE_NAME,
            description = description,
            socialTitle = title,
            // `canonical:` in front matter for a post that first appeared elsewhere.
            canonicalUrl = frontMatter?.get("canonical")?.singleOrNull(),
            // Every post gets a card named after its own route, drawn from this front
            // matter by assets/render-post-cards.sh and rendered in the same CI step as
            // the export. Derived rather than declared: a post that had to remember to
            // name its picture is a post that will forget.
            image = image,
            publishedTime = date,
            jsonLd = if (title != null && date != null) {
                blogPostingJsonLd(
                    headline = title,
                    description = description,
                    url = SITE_URL + route,
                    datePublished = date,
                    image = image,
                )
            } else {
                null
            },
        )
    )
}

/**
 * Wraps every generated Markdown page. Declares [PageLayout] as its parent so the
 * nav/footer chrome stays mounted while navigating between posts.
 */
@Layout(".components.layouts.PageLayout")
@Composable
fun MarkdownLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val frontMatter = ctx.markdown?.frontMatter
    val title = frontMatter?.get("title")?.singleOrNull()
    val date = frontMatter?.get("date")?.singleOrNull()
    val library = frontMatter?.get("library")?.singleOrNull()
    // `source:` is for a post about a repository that has no card on this site; a post about a
    // library gets the same link from its card. Either way the link sits under the headline,
    // not only in the footer: a reader arriving from a link aggregator reads the first screen.
    val source = frontMatter?.get("source")?.singleOrNull()
        ?: LIBRARIES.firstOrNull { it.name == library }?.repo

    if (title != null) {
        Header(attrs = { classes("post-header") }) {
            if (date != null) {
                // A machine-readable date, not a string that looks like one. The text
                // is already ISO, so `datetime` repeats it — that is fine and it is
                // what keeps the two from drifting when the display format changes.
                TagElement<org.w3c.dom.HTMLElement>("time", {
                    classes("post-date")
                    attr("datetime", date)
                }) { Text(date) }
            }
            H1 { Text(title) }
            if (source != null) {
                Div(attrs = { classes("hero-links", "post-source") }) {
                    Chip(source, source.removePrefix("https://").removePrefix("github.com/").let { "github/$it" })
                }
            }
        }
    }

    Article(attrs = { classes("prose") }) {
        content()
    }

    PostFooter(route = ctx.route.path.trimEnd('/'), libraryName = library)
}

/**
 * Where a post points once somebody has finished reading it.
 *
 * Without this every article is a dead end that offers GitHub and nothing else — a
 * reader who liked one of these has no path to the next, and neither does a crawler
 * following links to decide what else on the domain is worth its time.
 */
@Composable
private fun PostFooter(route: String, libraryName: String?) {
    val library = LIBRARIES.firstOrNull { it.name == libraryName }
    val alsoRead = BLOG_ENTRIES.filter { it.route != route }.take(3)

    Div(attrs = { classes("lib-more") }) {
        if (library != null) {
            Span(attrs = { classes("lib-more-label") }) { Text("the library this is about") }
            Div(attrs = { classes("hero-links") }) {
                Chip(library.page, library.name)
                Chip(library.repo, "github/${library.name}")
            }
        }

        if (alsoRead.isNotEmpty()) {
            Span(attrs = { classes("lib-more-label") }) { Text("also on this site") }
            Div(attrs = { classes("post-list") }) {
                alsoRead.forEach { entry ->
                    Link(entry.route, modifier = Modifier.classNames("post-item")) {
                        Div(attrs = { classes("post-title") }) { Text(entry.title) }
                        if (entry.description.isNotEmpty()) {
                            P(attrs = { classes("post-desc") }) { Text(entry.description) }
                        }
                    }
                }
            }
        }
    }
}
